import {FieldRequiredIndicator} from '../../components/form/field-required-indicator.js'
import {useFieldContext} from '../../integrations/form/index.js'
import {Field, Slider as ChakraSlider, SliderRootProps, Text, HStack, Box} from '@chakra-ui/react'
import {ErrorMessage} from './error-message.js'
import {FieldRoot} from './field-root.js'

interface Props extends Omit<SliderRootProps, 'value' | 'onValueChange' | 'onValueChangeEnd'> {
  label: string
  hideLabel?: boolean
  description?: string
  marks?: {value: number; label?: string}[]
  showValue?: boolean
  formatValue?: (value: number) => string
  required?: boolean
}

export function Slider({
  label,
  hideLabel,
  description,
  required,
  marks,
  showValue = false,
  formatValue,
  ...props
}: Props) {
  const field = useFieldContext<number>()

  const rawValue = field.state.value ?? props.min ?? 0
  const value = [rawValue]

  const displayValue = formatValue ? formatValue(rawValue) : String(rawValue)

  return (
    <FieldRoot required={required} disabled={props.disabled}>
      <ChakraSlider.Root
        name={field.name}
        value={value}
        width="100%"
        onValueChange={e => field.handleChange(e.value[0])}
        onValueChangeEnd={e => field.handleChange(e.value[0])}
        onBlur={field.handleBlur}
        {...props}
      >
        <Field.Label asChild>
          <ChakraSlider.Label srOnly={hideLabel}>
            <HStack justify="space-between" width="100%">
              <Box>
                {label} {required && <FieldRequiredIndicator />}
              </Box>
              {showValue && (
                <Text fontSize="sm" fontWeight="medium" color="fg.muted">
                  {displayValue}
                </Text>
              )}
            </HStack>
          </ChakraSlider.Label>
        </Field.Label>

        <ChakraSlider.Control>
          <ChakraSlider.Track>
            <ChakraSlider.Range bg="fg.muted" />
          </ChakraSlider.Track>
          <ChakraSlider.Thumb index={0} borderColor="fg.muted">
            <ChakraSlider.HiddenInput />
          </ChakraSlider.Thumb>
        </ChakraSlider.Control>

        {marks && (
          <ChakraSlider.MarkerGroup mt={10}>
            {marks.map(mark => (
              <ChakraSlider.Marker key={mark.value} value={mark.value}>
                {mark.label && (
                  <Text fontSize="xs" color="fg.muted">
                    {mark.label}
                  </Text>
                )}
              </ChakraSlider.Marker>
            ))}
          </ChakraSlider.MarkerGroup>
        )}
      </ChakraSlider.Root>

      <ErrorMessage />
      {description && (
        <Field.HelperText srOnly={hideLabel} mt={marks ? 6 : 0}>
          {description}
        </Field.HelperText>
      )}
    </FieldRoot>
  )
}
